import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Input, Button, Select, message, Typography, Avatar, Tag, Row, Col } from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  UserOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import userService from "../../services/userService";
import { useCommon } from "../../context/CommonContext";
const { Option } = Select;
const { Title, Text } = Typography;
const { Search } = Input;

function RolesList() {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} roles`,
  });

  const [filters, setFilters] = useState({
    searchText: "",
    status: "all",
  });

  const getRoles = useCallback(
    async (params = {}) => {
      try {
        setLoading(true);

        const requestParams = {
          perPage: pagination.pageSize,
          currentPage: pagination.current,
          searchText: filters.searchText,
          ...params,
        };

        console.log("Fetching roles with params:", requestParams);
        const response = await userService.getAllRoles(requestParams);
        console.log("Roles API Response:", response);

        if (response.data && response.data.status) {
          // Handle different possible response structures
          const rolesData =
            response.data.data?.data || response.data.data || [];
          const paginationData =
            response.data.data?.pagination || response.data.pagination || {};

          console.log("Roles data:", rolesData);
          console.log("Pagination data:", paginationData);

          // Ensure rolesData is an array
          if (Array.isArray(rolesData)) {
            setRoles(rolesData);
          } else {
            console.error("Roles data is not an array:", rolesData);
            setRoles([]);
          }

          setPagination((prev) => ({
            ...prev,
            ...paginationData,
            current: paginationData.currentPage || 1,
          }));
        } else {
          message.error("Failed to fetch roles");
          setRoles([]);
        }
      } catch (error) {
        console.error("Error fetching roles:", error);
        message.error("Failed to fetch roles");
        setRoles([]);
      } finally {
        setLoading(false);
      }
    },
    [filters.searchText, filters.status]
  );

  useEffect(() => {
    setPageTitle("Roles");
    getRoles();
  }, [getRoles]);

  const handleSearch = (value) => {
    setFilters((prev) => ({ ...prev, searchText: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getRoles({
      searchText: value,
      perPage: pagination.pageSize,
      currentPage: 1,
    });
  };

  const handleStatusChange = (value) => {
    setFilters((prev) => ({ ...prev, status: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getRoles({
      searchText: filters.searchText,
      status: value,
      perPage: pagination.pageSize,
      currentPage: 1,
    });
  };

  const handleAddRole = () => {
    navigate("/roles/add/new");
  };

  const handleEditRole = (roleId) => {
    navigate(`/roles/edit/${roleId}`);
  };

  const handleMenuAccess = (roleId) => {
    navigate(`/roles/menu-access/${roleId}`);
  };

  // Role Card Component
  const RoleCard = ({ role }) => (
    <Card
      hoverable
      style={{
        height: "100%",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        transition: "all 0.3s ease",
      }}
      bodyStyle={{
        padding: "20px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "12px",
          }}
        >
          <Avatar
            icon={<UserOutlined />}
            style={{
              backgroundColor: role.is_active === "Y" ? "#52c41a" : "#ff4d4f",
              marginRight: "12px",
            }}
          />
          <div>
            <Text strong style={{ fontSize: "16px", display: "block" }}>
              {role.name.charAt(0).toUpperCase() +
                role.name.slice(1).toLowerCase()}
            </Text>
            <Tag
              color={role.is_active ? "green" : "red"}
              style={{ marginTop: "4px" }}
            >
              {role.is_active ? "Active" : "Inactive"}
            </Tag>
          </div>
        </div>
      </div>

      <div style={{ marginTop: "16px", textAlign: "right", display: "flex", gap: "16px" }}>
        <Button
          type="primary"
          icon={<MenuOutlined />}
          onClick={() => handleMenuAccess(role.id)}
          size="small"
          style={{ borderRadius: "6px" }}
        >
          Menu Access
        </Button>
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => handleEditRole(role.id)}
          size="small"
          style={{ borderRadius: "6px" }}
        >
          Edit
        </Button>
      </div>
    </Card>
  );

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Roles ({pagination.total})
        </Title>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            minWidth: 300,
            flex: 1,
            justifyContent: "flex-end",
          }}
        >
          <Select
            value={filters.status}
            onChange={handleStatusChange}
            placeholder="All"
            style={{ width: 140 }}
          >
            <Option value="all">All</Option>
            <Option value="Y">Active</Option>
            <Option value="N">Inactive</Option>
          </Select>
          <Search
            placeholder="Search roles..."
            allowClear
            onSearch={handleSearch}
            style={{ width: 320, maxWidth: "40vw" }}
            enterButton={<SearchOutlined />}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddRole}
            size="middle"
          >
            Add Role
          </Button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <div>Loading roles...</div>
        </div>
      ) : roles.length === 0 ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Text type="secondary">No roles found</Text>
        </div>
      ) : (
        <>
          <Row gutter={[16, 16]}>
            {roles.map((role) => (
              <Col
                key={role.id}
                xs={24} // 1 card per row on extra small screens
                sm={12} // 2 cards per row on small screens
                md={8} // 3 cards per row on medium screens
              >
                <RoleCard role={role} />
              </Col>
            ))}
          </Row>

          {/* Pagination */}
          <div
            style={{
              marginTop: "24px",
              textAlign: "center",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <Button
              disabled={pagination.current === 1}
              onClick={() => {
                const newPage = pagination.current - 1;
                setPagination((prev) => ({ ...prev, current: newPage }));
                getRoles({
                  perPage: pagination.pageSize,
                  currentPage: newPage,
                  searchText: filters.searchText,
                  status: filters.status,
                });
              }}
            >
              Previous
            </Button>

            <Text>
              Page {pagination.current} of{" "}
              {Math.ceil(pagination.total / pagination.pageSize)}
            </Text>

            <Button
              disabled={
                pagination.current >=
                Math.ceil(pagination.total / pagination.pageSize)
              }
              onClick={() => {
                const newPage = pagination.current + 1;
                setPagination((prev) => ({ ...prev, current: newPage }));
                getRoles({
                  perPage: pagination.pageSize,
                  currentPage: newPage,
                  searchText: filters.searchText,
                  status: filters.status,
                });
              }}
            >
              Next
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default RolesList;
